import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { installmentService } from '@services/installmentService';
import toast from 'react-hot-toast';

const QUERY_KEY = 'installments';

export const useInstallments = (includeCompleted = false) => {
  const queryClient = useQueryClient();

  const {
    data: installments = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: [QUERY_KEY, includeCompleted],
    queryFn: () => installmentService.getAll(includeCompleted),
  });

  const createMutation = useMutation({
    mutationFn: installmentService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Parcelamento criado com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao criar parcelamento');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ installmentId, data }) => installmentService.update(installmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Parcelamento atualizado!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao atualizar');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: installmentService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast.success('Parcelamento excluído!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao excluir');
    },
  });

  const payMutation = useMutation({
    mutationFn: ({ installmentId, installmentNumber, paymentDate }) =>
      installmentService.pay(installmentId, installmentNumber, paymentDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Parcela paga com sucesso!');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error?.message || 'Erro ao pagar parcela');
    },
  });

  // Calcular totais
  const totalAmount = installments.reduce((sum, i) => sum + i.totalAmount, 0);
  const remainingAmount = installments.reduce((sum, i) => sum + i.remainingAmount, 0);
  const paidAmount = totalAmount - remainingAmount;

  return {
    installments,
    totalAmount,
    remainingAmount,
    paidAmount,
    isLoading,
    error,
    refetch,
    createInstallment: createMutation.mutateAsync,
    updateInstallment: updateMutation.mutateAsync,
    deleteInstallment: deleteMutation.mutateAsync,
    payInstallment: payMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isPaying: payMutation.isPending,
  };
};

export default useInstallments;
